import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Scale, Users, FileText, Bell, Settings, LogOut, Home, Building2, Star, ChevronDown, ChevronRight, FileBarChart, Shield, Eye, Briefcase, Menu, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { GlobalSearch } from './GlobalSearch';
import { GlobalJobTicker } from './GlobalJobTicker';
import { apiClient } from '../api/client';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from './ui/sheet';
import { ImportQueueWidget } from './ImportQueueWidget';


export const Layout = ({ children }) => {
  const { user, tenant, token, logout, isAdmin, isOperador, isCliente, isSuperAdmin, isCompany, isLawFirm, isEndUser, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // DEBUG: Diagnose permissions issue
  useEffect(() => {
    console.log('Layout Debug:', {
      role: user?.role,
      tenantId: user?.tenant_id,
      tenantType: tenant?.tenant_type,
      path: location.pathname,
      isEndUser: isEndUser(),
      isSuperAdmin: isSuperAdmin(),
      isPlatformRoute: location.pathname.startsWith("/platform"),
      isAdmin: isAdmin(),
      isCliente: isCliente(),
      canTrademarks: can('trademarks')
    });
  }, [user, tenant, location.pathname]);

  // Sidebar state
  const [favoriteClients, setFavoriteClients] = useState([]);
  const [favoritesExpanded, setFavoritesExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const fetchFavorites = async () => {
    try {
      const res = await apiClient.get('/favorites/clients');
      setFavoriteClients(res.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  useEffect(() => {
    if (!isCliente() && token) {
      fetchFavorites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Phase 4: Dynamic Navigation
  const getNavigation = () => {
    // 1. Tenant Menu (Base)
    const base = [
      { name: 'Dashboard', href: '/dashboard', icon: Home, show: true },
      { name: 'Marcas', href: '/trademarks', icon: FileText, show: can('trademarks') },
      { name: 'Riesgo Marcario', href: '/risk', icon: ShieldCheck, show: can('trademarks') },
      { name: isCompany() ? 'Portafolios' : 'Clientes', href: '/clients', icon: Building2, show: !isEndUser() && can('clients') },
      { name: 'Despachos', href: '/shared/lawfirms', icon: Briefcase, show: isCompany() && can('lawfirms') },
      { name: 'Procedimientos', href: '/procedures', icon: Scale, show: !isEndUser() && can('procedures') },
      { name: 'Alertas', href: '/alerts', icon: Bell, show: can('alerts') },
      { name: 'Gaceta', href: '/gazette', icon: Eye, show: can('gazette') },
      { name: 'Reportes', href: '/reports', icon: FileBarChart, show: !isEndUser() && can('reports') },
      { name: 'Usuarios', href: '/users', icon: Users, show: isAdmin() && !isEndUser() && can('users') },
      { name: 'Configuración', href: '/settings', icon: Settings, show: isAdmin() },
      { name: 'Plataforma', href: '/platform', icon: Shield, show: isSuperAdmin() },
    ];

    // 2. Platform Menu (Super Admin @ /platform)
    const platformMenu = [
      { name: 'Overview', href: '/platform/overview', icon: FileBarChart, show: true },
      { name: 'Tenants', href: '/platform/tenants', icon: Building2, show: true },
      { name: 'Usuarios Globales', href: '/platform/users', icon: Users, show: true },
      { name: 'Planes', href: '/platform/plans', icon: Star, show: true },
      { name: 'Links B2B', href: '/platform/links', icon: FileText, show: true },
    ];

    // 3. Selection Logic (Explicitly Paranoid)
    if (user?.role === 'SUPER_ADMIN') {
      return platformMenu;
    }

    // Default: Tenant Menu (Law Firm / Company / etc.)
    // Ensure no platform links leak here, and filter by 'show'
    return base.filter(item => item.show && !item.href?.startsWith('/platform'));
  };

  const navigation = getNavigation();

  const isActive = (href) => location.pathname === href;
  const isTrademarksActive = location.pathname.startsWith('/trademarks');

  // Determine search placeholder based on route
  const getSearchPlaceholder = () => {
    if (location.pathname.startsWith('/platform/tenants')) return "Buscar tenant...";
    if (location.pathname.startsWith('/platform/users')) return "Buscar usuario global...";
    return "Buscar marca...";
  };

  const renderNavItems = () => {
    return navigation.filter(item => item.show).map((item) => {
      const Icon = item.icon;
      const active = isActive(item.href);

      // Special handling for Marcas with favorites submenu
      if (item.name === 'Marcas' && !isCliente() && favoriteClients.length > 0) {
        return (
          <div key={item.name}>
            <button
              onClick={() => { navigate(item.href); setIsMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${isTrademarksActive
                ? 'bg-gray-100 text-gray-900 font-medium'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              <Icon className="h-5 w-5" />
              {item.name}
            </button>

            {/* Favorites Submenu */}
            <div className="ml-4 mt-1">
              <button
                onClick={() => setFavoritesExpanded(!favoritesExpanded)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md"
              >
                <Star className="h-3.5 w-3.5" />
                <span>Favoritos</span>
                {favoritesExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5 ml-auto" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 ml-auto" />
                )}
              </button>

              {favoritesExpanded && (
                <div className="mt-1 space-y-0.5">
                  {favoriteClients.map(fav => (
                    <button
                      key={fav.id}
                      onClick={() => { navigate(`/trademarks?client_id=${fav.client_id}`); setIsMobileMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md truncate"
                      title={fav.client_nombre}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0"></span>
                      <span className="truncate">{fav.client_nombre}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <button
          key={item.name}
          onClick={() => { navigate(item.href); setIsMobileMenuOpen(false); }}
          className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${active
            ? 'bg-gray-100 text-gray-900 font-medium'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
        >
          <Icon className="h-5 w-5" />
          {item.name}
        </button>
      );
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <GlobalJobTicker />

      {/* Desktop Sidebar (HIDDEN on mobile) */}
      <div className="hidden lg:flex w-64 bg-white border-r border-gray-200 flex-col flex-shrink-0">
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <Scale className="h-6 w-6 text-indigo-600 mr-2" />
          <span className="text-lg font-semibold text-gray-900">IMPI Monitor</span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {renderNavItems()}
        </nav>

        {/* User section (Desktop) */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.nombre}</p>
              <p className="text-xs text-gray-500 truncate capitalize">{user?.role?.toLowerCase()?.replace('_', ' ')}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8 text-gray-500">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar (Responsive) */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-4 md:px-8 gap-4 flex-shrink-0">
          {/* Mobile Menu Trigger */}
          <div className="lg:hidden">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 h-full flex flex-col">
                <SheetHeader className="px-6 py-5 border-b text-left">
                  <SheetTitle className="flex items-center gap-2">
                    <Scale className="h-6 w-6 text-indigo-600" />
                    <span>IMPI Monitor</span>
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                  {renderNavItems()}
                </nav>
                <div className="p-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{user?.nombre}</p>
                      <p className="text-xs text-gray-500 truncate capitalize">{user?.role?.toLowerCase()?.replace('_', ' ')}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8 text-gray-500">
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="hidden md:flex flex-shrink-0">
            {/* Removing "Monitor" title as requested */}
          </div>

          <div className="flex items-center gap-4 flex-1">
            <ImportQueueWidget />
            <div className="flex-1 max-w-lg">
              <GlobalSearch placeholder={getSearchPlaceholder()} />
            </div>
          </div>
        </header>

        {/* Page content scroll area */}
        <main className="flex-1 overflow-auto bg-gray-50/50">
          <div className="max-w-7xl mx-auto p-4 md:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
